// v1.33: /ocr — Business Card OCR 3-step 上传流程
// Step 1: 选/拖文件 + 预览
// Step 2: AI 解析后, 15 字段可编辑表单
// Step 3: 点"导入客户" → POST /api/ocr/business-card/import → 跳 /customers/:id
//
// 设计: Wabi-sabi 茶色 #1a1a1a + 暖白 #fdfaf6, Tailwind 已有 token
// 移动端: 全宽 stack + 44px 触达 + 大预览
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { type BusinessCardFields, ocrApi } from '../lib/api';

export const Route = createFileRoute('/ocr')({
  component: OcrPage,
});

const COUNTRIES = [
  { code: 'JP', name: '日本' },
  { code: 'US', name: '美国' },
  { code: 'CN', name: '中国' },
  { code: 'GB', name: '英国' },
  { code: 'DE', name: '德国' },
  { code: 'KR', name: '韩国' },
  { code: 'FR', name: '法国' },
  { code: 'IT', name: '意大利' },
];

const EMPTY_FIELDS: BusinessCardFields = {
  company: null,
  contactName: null,
  title: null,
  email: null,
  phone: null,
  whatsapp: null,
  wechat: null,
  country: null,
  city: null,
  address: null,
  industry: null,
  tags: [],
  confidence: null,
};

type Step = 'upload' | 'review' | 'importing' | 'done';

export function OcrPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [fields, setFields] = useState<BusinessCardFields>(EMPTY_FIELDS);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // preview URL lifecycle — 用 dataURL fallback 兼容 jsdom (URL.createObjectURL 缺失)
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    if (typeof URL.createObjectURL === 'function') {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    // jsdom / 旧浏览器 fallback: FileReader → dataURL
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
    return undefined;
  }, [file]);

  // 上传 + OCR 解析
  const uploadMutation = useMutation({
    mutationFn: (f: File) => ocrApi.upload(f),
    onSuccess: (res) => {
      setJobId(res.jobId);
      setImageUrl(res.imageUrl);
      setFields({
        ...EMPTY_FIELDS,
        ...res.fields,
        // AI 返空字符串归 null
        company: res.fields.company || null,
        contactName: res.fields.contactName || null,
      });
      if (res.status === 'failed') {
        setError(res.errorMessage ?? 'AI 未能识别名片内容, 请手动填写');
      }
      setStep('review');
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  // 导入到客户库
  const importMutation = useMutation({
    mutationFn: () =>
      ocrApi.import({
        fields,
        jobId: jobId ?? undefined,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      queryClient.invalidateQueries({ queryKey: ['ocr-jobs'] });
      setStep('done');
      // 跳客户详情
      setTimeout(() => {
        void navigate({ to: '/customers/$customerId', params: { customerId: res.customerId } });
      }, 800);
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  // v1.33 fix: 文件确认后直接 mutate (避免 useEffect 时序问题)
  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('仅支持 PNG / JPG / WebP 格式');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('文件超过 5MB');
      return;
    }
    setError(null);
    setFile(f);
    uploadMutation.mutate(f);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      setError('仅支持 PNG / JPG / WebP 格式');
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      setError('文件超过 5MB');
      return;
    }
    setError(null);
    setFile(f);
    uploadMutation.mutate(f);
  }

  function handleStartOver() {
    setFile(null);
    setFields(EMPTY_FIELDS);
    setJobId(null);
    setImageUrl(null);
    setError(null);
    setStep('upload');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ============================================================
  // Step 1: Upload
  // ============================================================
  if (step === 'upload') {
    return (
      <div className="max-w-2xl mx-auto animate-fade-up">
        <div className="mb-4 flex items-center gap-2 text-sm">
          <Link
            to="/ocr/history"
            className="inline-flex items-center gap-1 text-ink-3 hover:text-accent min-h-[44px] px-2 -ml-2"
          >
            <Icon name="clock" size="sm" aria-hidden />
            OCR 历史
          </Link>
        </div>
        <header className="mb-5">
          <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight flex items-center gap-2">
            <Icon name="sparkles" size="md" className="text-accent" aria-hidden />
            名片识别 (OCR)
          </h1>
          <p className="text-xs sm:text-sm text-ink-3 mt-1 leading-relaxed">
            拍名片上传 — AI 自动识别公司/联系人/邮箱/电话等 13 字段, 可编辑后一键导入客户库。
          </p>
        </header>

        <Card padding="lg">
          <button
            type="button"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-line rounded-lg p-8 sm:p-12 text-center hover:border-accent/40 transition-colors cursor-pointer w-full"
            onClick={() => fileInputRef.current?.click()}
            aria-label="点击或拖拽上传名片图片"
          >
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-accent-soft text-accent flex items-center justify-center">
                <Icon name="upload" size="lg" aria-hidden />
              </div>
              <div>
                <p className="text-sm text-ink-2 font-medium">点击上传 或 拖拽图片到此处</p>
                <p className="text-xs text-ink-3 mt-1">PNG / JPG / WebP · 最大 5MB</p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleFileChange}
              data-testid="ocr-file-input"
            />
          </button>

          {error && (
            <div className="mt-4">
              <Alert variant="error">{error}</Alert>
            </div>
          )}
        </Card>

        <p className="text-xs text-ink-3 mt-3 leading-relaxed">
          由 Cloudflare Workers AI (llama-3.2-11b-vision) 提供识别 · 免费层 10K 图片/天
        </p>
      </div>
    );
  }

  // ============================================================
  // Step 2: Review & Edit (also handles Step 3 import)
  // ============================================================
  return (
    <div className="max-w-3xl mx-auto animate-fade-up">
      <header className="mb-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-ink leading-tight flex items-center gap-2">
          <Icon name="sparkles" size="md" className="text-accent" aria-hidden />
          识别结果
        </h1>
        <p className="text-xs sm:text-sm text-ink-3 mt-1 leading-relaxed">
          校对 AI 识别的字段, 确认后导入客户库 (会自动建询盘)。
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        {/* 左: 名片预览 */}
        <Card padding="sm" className="h-fit md:sticky md:top-20">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="名片原图"
              className="w-full rounded border border-line"
              referrerPolicy="no-referrer"
            />
          ) : previewUrl ? (
            <img src={previewUrl} alt="名片预览" className="w-full rounded border border-line" />
          ) : null}
          {fields.confidence !== null && (
            <p className="text-[10px] text-ink-3 mt-2 text-center">
              AI 置信度: {Math.round(fields.confidence * 100)}%
            </p>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleStartOver}
            className="w-full mt-2"
            iconLeft="x"
          >
            重新上传
          </Button>
        </Card>

        {/* 右: 字段表单 */}
        <div className="space-y-3">
          {error && step === 'review' && !uploadMutation.isError && (
            <Alert variant="info">{error}</Alert>
          )}
          {importMutation.isError && (
            <Alert variant="error">{(importMutation.error as Error).message}</Alert>
          )}

          <Card padding="md">
            <h2 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-1.5">
              <Icon name="building" size="sm" className="text-accent" aria-hidden />
              公司 / 联系人
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="公司名 *"
                value={fields.company}
                onChange={(v) => setFields({ ...fields, company: v })}
                placeholder="例: 株式会社トイアルファ"
                required
                testid="ocr-field-company"
              />
              <Field
                label="联系人"
                value={fields.contactName}
                onChange={(v) => setFields({ ...fields, contactName: v })}
                placeholder="例: 田中太郎"
                testid="ocr-field-name"
              />
              <Field
                label="职位 / 头衔"
                value={fields.title}
                onChange={(v) => setFields({ ...fields, title: v })}
                placeholder="例: 営業部長 / CEO"
                testid="ocr-field-title"
              />
              <Field
                label="行业"
                value={fields.industry}
                onChange={(v) => setFields({ ...fields, industry: v })}
                placeholder="toys / figure / plush / ..."
                testid="ocr-field-industry"
              />
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-1.5">
              <Icon name="mail" size="sm" className="text-accent" aria-hidden />
              联系方式
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="邮箱"
                value={fields.email}
                onChange={(v) => setFields({ ...fields, email: v })}
                placeholder="example@company.com"
                type="email"
                testid="ocr-field-email"
              />
              <Field
                label="电话"
                value={fields.phone}
                onChange={(v) => setFields({ ...fields, phone: v })}
                placeholder="+81-3-1234-5678"
                testid="ocr-field-phone"
              />
              <Field
                label="WhatsApp"
                value={fields.whatsapp}
                onChange={(v) => setFields({ ...fields, whatsapp: v })}
                placeholder="+81-90-..."
                testid="ocr-field-whatsapp"
              />
              <Field
                label="微信"
                value={fields.wechat}
                onChange={(v) => setFields({ ...fields, wechat: v })}
                placeholder="微信号"
                testid="ocr-field-wechat"
              />
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-1.5">
              <Icon name="globe" size="sm" className="text-accent" aria-hidden />
              地址
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs text-ink-2">
                国家
                <select
                  value={fields.country ?? 'JP'}
                  onChange={(e) => setFields({ ...fields, country: e.target.value })}
                  className="px-3 py-2 border border-line rounded text-sm bg-surface min-h-[44px]"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <Field
                label="城市"
                value={fields.city}
                onChange={(v) => setFields({ ...fields, city: v })}
                placeholder="例: 東京"
                testid="ocr-field-city"
              />
              <div className="sm:col-span-2">
                <Field
                  label="详细地址"
                  value={fields.address}
                  onChange={(v) => setFields({ ...fields, address: v })}
                  placeholder="街道 / 门牌号"
                  testid="ocr-field-address"
                />
              </div>
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-sm font-semibold text-ink-2 mb-3 flex items-center gap-1.5">
              <Icon name="tag" size="sm" className="text-accent" aria-hidden />
              标签
            </h2>
            <Field
              label="标签 (英文逗号分隔)"
              value={fields.tags.join(', ')}
              onChange={(v) =>
                setFields({
                  ...fields,
                  tags: v
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean),
                })
              }
              placeholder="VIP, 玩具, 复购"
              testid="ocr-field-tags"
            />
          </Card>

          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end pt-2">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={handleStartOver}
              iconLeft="x"
            >
              取消
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() => {
                setError(null);
                importMutation.mutate();
              }}
              loading={importMutation.isPending || step === 'importing'}
              disabled={!fields.company && !fields.contactName}
              iconLeft="check"
              data-testid="ocr-import-btn"
            >
              {importMutation.isPending || step === 'importing' ? '导入中…' : '导入客户库'}
            </Button>
          </div>

          {step === 'done' && <Alert variant="success">导入成功 — 正在跳转到客户详情…</Alert>}
        </div>
      </div>
    </div>
  );
}

// 字段小组件
function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  type = 'text',
  testid,
}: {
  label: string;
  value: string | null;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  type?: string;
  testid?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-2">
      {label}
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="px-3 py-2 border border-line rounded text-sm bg-surface min-h-[44px] focus:border-accent focus:outline-none"
      />
    </label>
  );
}
